import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { CreateIncomingRequests, GetMonazamBankAccountsForHGO } from "../../../../Actions/RequestActions";
import { getTimeAddedDate, SuccessToast, WarningToast } from "../../../../Util";
import { Spinner } from "react-bootstrap";
import "./IncomingRequest.css";

const defaultValues = {
  date: "",
  narration: "",
  amount: "",
  currency: "",
  hajiName: "",
  bankName: "",
  transactionId: "",
  monazamBankAccountId: "",
};

const IncomingRequest = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(defaultValues);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiErrors, setApiErrors] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]); // full objects from API
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState("");
  const [fcyAccount, setFcyAccount] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = () => {
    SuccessToast("Incoming request created successfully!");
    setFormData(defaultValues);
    setValidationErrors({});
    setApiErrors({});
    setMessage(null);
    onClose("create");
  };

  const mapRawErrorsToFields = (rawErrors) => {
    const fieldErrors = {};
    const generalErrors = [];

    rawErrors.forEach((error) => {
      const errorLower = error.toLowerCase();
      
      if (errorLower.includes("date")) {
        fieldErrors.date = error;
      } else if (errorLower.includes("narration")) {
        fieldErrors.narration = error;
      } else if (errorLower.includes("currency")) {
        fieldErrors.currency = error;
      } else if (errorLower.includes("amount")) {
        fieldErrors.amount = error;
      } else if (errorLower.includes("type")) {
        fieldErrors.type = error;
      } else if (errorLower.includes("time")) {
        fieldErrors.time = error;
      } else if (errorLower.includes("hajiname") || errorLower.includes("haji name")) {
        fieldErrors.hajiName = error;
      } else if (errorLower.includes("bankname") || errorLower.includes("bank name")) {
        fieldErrors.bankName = error;
      } else if (errorLower.includes("transactionid") || errorLower.includes("transaction id")) {
        fieldErrors.transactionId = error;
      } else if (errorLower.includes("monazambankaccountid") || errorLower.includes("monazzam bank account")) {
        fieldErrors.monazamBankAccountId = error;
      } else {
        generalErrors.push(error);
      }
    });

    return { fieldErrors, generalErrors };
  };

  useEffect(() => {
    if (isOpen) {
      GetMonazamBankAccountsForHGO(
        (data) => {
          if (data?.bankAccounts) {
            setBankAccounts(data.bankAccounts);
          }
          if (data?.fcyAccount) {
            setFcyAccount(data.fcyAccount);
          }
        },
        setBankLoading,
        setBankError
      );
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.currency === "USD" && fcyAccount && !formData.bankName) {
      setFormData((prev) => ({
        ...prev,
        bankName: fcyAccount.accountTitle || "FCY Bank Account",
        monazamBankAccountId: ""
      }));
    }
  }, [formData.currency, fcyAccount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount") {
      let value = e.target.value;
      value = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/(\.\d{2})\d+/, "$1");
      const numValue = value === "" ? "" : parseFloat(value);
      setFormData((prevData) => ({
        ...prevData,
        [name]: numValue,
      }));
    } else {
      setFormData((prevData) => {
        const newData = {
          ...prevData,
          [name]: value,
        };
        // Reset bank selection and account ID when currency changes
        if (name === "currency") {
          newData.bankName = "";
          newData.monazamBankAccountId = "";
          // If switching to USD, ensure monazamBankAccountId stays cleared
          if (value === "USD") {
            newData.monazamBankAccountId = "";
          }
        } else if (name === "bankName" && prevData.currency === "PKR") {
          // When PKR account is selected, find and set the account ID
          const selectedAccount = bankAccounts.find(
            acc => acc.accountType === "PKR" && (acc.bankName === value || acc.accountTitle === value)
          );
          if (selectedAccount && selectedAccount.id) {
            newData.monazamBankAccountId = selectedAccount.id;
          } else {
            newData.monazamBankAccountId = "";
          }
        }
        return newData;
      });
    }
    setValidationErrors((prev) => ({ ...prev, [name]: false }));
    setApiErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  useEffect(() => {
    let tooltipInstance = null;
    const triggerEl = document.getElementById("bankTooltip");
    if (isOpen && !formData.currency && window.bootstrap && triggerEl) {
      tooltipInstance = new window.bootstrap.Tooltip(triggerEl, {
        trigger: "hover",
        delay: { show: 50, hide: 0 },
        container: "body",
        placement: "top",
      });
    }
    return () => {
      if (tooltipInstance) tooltipInstance.dispose();
    };
  }, [isOpen, formData.currency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    const missingFields = [];
    const { date, narration, currency, amount, hajiName, bankName, transactionId } = formData;

    if (!date) {
      errors.date = true;
      missingFields.push("Date");
    }
    if (!narration?.trim()) {
      errors.narration = true;
      missingFields.push("Narration");
    }
    if (!currency) {
      errors.currency = true;
      missingFields.push("Currency");
    }
    if (!amount) {
      errors.amount = true;
      missingFields.push("Amount");
    } else if (amount <= 0) {
      errors.amount = true;
    }

    // Check for PKR currency and missing Monazzam Account
    if (currency === "PKR" && !formData.monazamBankAccountId) {
      errors.monazamBankAccountId = true;
      missingFields.push("Monazzam Account");
    }

    // Optional fields validation (if provided)
    if (hajiName && hajiName.trim().length > 255) {
      errors.hajiName = true;
    }
    if (bankName && bankName.trim().length > 255) {
      errors.bankName = true;
    }
    if (transactionId && transactionId.trim().length > 255) {
      errors.transactionId = true;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 8);

      const submitData = {
        ...formData,
        date: getTimeAddedDate(formData.date),
        time: currentTime,
        type: "incoming",
      };

      if (currency === "PKR" && formData.monazamBankAccountId) {
        submitData.monazamBankAccountId = formData.monazamBankAccountId;
      } else {
        delete submitData.monazamBankAccountId;
      }

      setSubmitting(true);
      CreateIncomingRequests(submitData, setMessage, () => {
        setSubmitting(false);
        handleCreate();
      });
    } else {
      if (missingFields.length > 0) {
        const fieldList = missingFields.join(", ");
        WarningToast(`${fieldList} must not be empty.`);
      } else if (errors.amount && amount <= 0) {
        WarningToast("Amount must be a positive number.");
      } else {
        WarningToast("Please fix the validation errors.");
      }
    }
  };

  useEffect(() => {
    if (message && message.type === "error") {
      setSubmitting(false);
      if (message.rawErrors && message.rawErrors.length > 0) {
        const { fieldErrors, generalErrors } = mapRawErrorsToFields(message.rawErrors);
        setApiErrors(fieldErrors);
        
        if (generalErrors.length > 0) {
          generalErrors.forEach(error => WarningToast(error));
        }
        
        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            Object.keys(fieldErrors).forEach((field) => {
              newErrors[field] = true;
            });
            return newErrors;
          });
        }
      } else if (message.message) {
        WarningToast(message.message);
      }
    }
  }, [message]);

  if (!isOpen) return <></>;

  return (
    <div className="modal d-block modal-blur-bg incoming-request-modal">
      <div className="modal-dialog modal-dialog-centered">
        <div className="px-5 modal-content">
          <button
            className="btn modal-label-txt modal-btn-bg btn-success position-absolute top-0 end-0 m-3"
            onClick={() => {
              onClose("close");
            }}
          >
            Close <FaTimes className="me-1" />
          </button>

          <div className="modal-header">
            <h2 className="modal-title mx-auto modal-heading-txt">
              Incoming Request
            </h2>
          </div>
          <div className="modal-body">
            <div className="d-flex justify-content-between">
              <div className="flex-grow-1 me-2">
                <label className="modal-label-txt">Date *</label>
                <input
                  type="date"
                  name="date"
                  className={`form-control modal-input-txt ${validationErrors.date || apiErrors.date ? 'is-invalid' : ''}`}
                  value={formData.date}
                  onChange={handleChange}
                  onClick={(e) => e.target.showPicker()}
                />
                {apiErrors.date && (
                  <div className="invalid-feedback d-block">
                    {apiErrors.date}
                  </div>
                )}
              </div>
              <div className="flex-grow-1 ms-2">
                <label className="modal-label-txt">Currency *</label>
                <select
                  name="currency"
                  className={`form-select modal-input-txt ${validationErrors.currency || apiErrors.currency ? 'is-invalid' : ''}`}
                  value={formData.currency}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                </select>
                {apiErrors.currency && (
                  <div className="invalid-feedback d-block">
                    {apiErrors.currency}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="modal-label-txt">Transaction ID</label>
              <input
                type="text"
                name="transactionId"
                className={`form-control modal-input-txt ${validationErrors.transactionId || apiErrors.transactionId ? 'is-invalid' : ''}`}
                placeholder="BTRX/PK/2025/1001/0001"
                value={formData.transactionId}
                onChange={handleChange}
              />
              {apiErrors.transactionId && (
                <div className="invalid-feedback d-block">
                  {apiErrors.transactionId}
                </div>
              )}
            </div>

            {formData.currency === "PKR" ? (
              <div className="d-flex justify-content-between">
                <div className="flex-grow-1 me-2">
                  <label className="modal-label-txt">Hajji</label>
                  <input
                    type="text"
                    name="hajiName"
                    className={`form-control modal-input-txt ${validationErrors.hajiName || apiErrors.hajiName ? 'is-invalid' : ''}`}
                    placeholder="Name"
                    value={formData.hajiName}
                    onChange={handleChange}
                  />
                  {apiErrors.hajiName && (
                    <div className="invalid-feedback d-block">
                      {apiErrors.hajiName}
                    </div>
                  )}
                </div>
                <div className="flex-grow-1 ms-2">
                  <label className="modal-label-txt">Monazzam Account</label>
                  {(() => {
                    const bankDisabled = bankLoading || !formData.currency;
                    let filteredBanks = [];
                    if (formData.currency === "PKR") {
                      filteredBanks = bankAccounts.filter(acc => acc.accountType === "PKR");
                    }
                    
                    const hasBanksForCurrency = filteredBanks.length > 0;
                    
                    return (
                      <div>
                        <div
                          id="bankTooltip"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-title={!formData.currency ? "Select currency first" : ""}
                        >
                          <select
                            name="bankName"
                            className={`form-select modal-input-txt ${validationErrors.monazamBankAccountId || apiErrors.monazamBankAccountId ? 'is-invalid' : ''}`}
                            value={formData.bankName}
                            onChange={handleChange}
                            disabled={bankDisabled}
                          >
                            <option value="" disabled hidden>
                              {bankLoading ? "Loading accounts..." : "Select"}
                            </option>
                            {formData.currency === "PKR" && !hasBanksForCurrency && (
                              <option disabled>No PKR accounts available</option>
                            )}
                            {filteredBanks.map((acc) => (
                              <option 
                                key={acc.id} 
                                value={acc.bankName || acc.accountTitle}
                                data-account-id={acc.id}
                              >
                                {acc.bankName || acc.accountTitle}
                              </option>
                            ))}
                          </select>
                        </div>
                        {apiErrors.monazamBankAccountId && (
                          <div className="invalid-feedback d-block">
                            {apiErrors.monazamBankAccountId}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div>
                <label className="modal-label-txt">Hajji</label>
                <input
                  type="text"
                  name="hajiName"
                  className={`form-control modal-input-txt ${validationErrors.hajiName || apiErrors.hajiName ? 'is-invalid' : ''}`}
                  placeholder="Name"
                  value={formData.hajiName}
                  onChange={handleChange}
                />
                {apiErrors.hajiName && (
                  <div className="invalid-feedback d-block">
                    {apiErrors.hajiName}
                  </div>
                )}
              </div>
            )}

            {bankError && (
              <div className="alert alert-warning" role="alert">
                <small>⚠️ {bankError}</small>
              </div>
            )}

            <div>
              <label className="modal-label-txt">Narration *</label>
              <textarea
                name="narration"
                className={`form-control modal-input-txt ${validationErrors.narration || apiErrors.narration ? 'is-invalid' : ''}`}
                rows="3"
                placeholder="Comments"
                value={formData.narration}
                onChange={handleChange}
              />
              {apiErrors.narration && (
                <div className="invalid-feedback d-block">
                  {apiErrors.narration}
                </div>
              )}
            </div>

            <div>
              <label className="modal-label-txt">Amount *</label>
              <input
                type="number"
                name="amount"
                className={`form-control modal-input-txt ${validationErrors.amount || apiErrors.amount ? 'is-invalid' : ''}`}
                placeholder="20,555"
                value={formData.amount}
                onChange={handleChange}
              />
              {apiErrors.amount && (
                <div className="invalid-feedback d-block">
                  {apiErrors.amount}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer justify-content-center">
            <button
              className="btn w-50 modal-label-txt modal-btn-bg d-flex justify-content-center align-items-center"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Submit"
              )}
            </button>
          </div>

          {message && (
            <div
              className={`alert mt-3 ${
                message.type === "success" ? "alert-success" : "alert-danger"
              }`}
            >
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncomingRequest;
